import React, { CSSProperties, type ElementType, type Ref } from 'react'
import classNames from 'classnames'
import type { Media as MediaType } from '@/payload-types'
import NextImage, { ImageProps, StaticImageData } from 'next/image'
import { cn } from '@/utils/ui'
import { getClientSideURL } from '@/utils/getURL'
import { cssVariables } from '@/cssVariables'
import * as url from 'node:url'

const placeholderBlur =
  'data:image/png;base64,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'

const { breakpoints } = cssVariables

export type Props = {
  alt?: string | null
  className?: string
  style?: CSSProperties
  fill?: boolean // for NextImage only
  htmlElement?: ElementType | null
  onClick?: () => void
  onLoad?: () => void
  loading?: 'lazy' | 'eager' // for NextImage only
  priority?: boolean // for NextImage only
  ref?: Ref<HTMLImageElement | HTMLVideoElement | null>
  resource?: MediaType | string | null // for Payload media
  size?: string // for NextImage only
  src?: StaticImageData // for static media
  height?: number
  width?: number
  captionPlacement?: 'inside' | 'outside' | null
  caption?: string | null
  copyright?: string | null
}

export const CMSImage: React.FC<Props> = (props) => {
  const {
    fill,
    loading,
    priority,
    alt: altFromProps,
    resource,
    src: srcFromProps,
    size: sizesFromProps,
    className: classNameFromProps,
    style: styleFromProps,
    width: widthFromProps,
    height: heightFromProps,
    copyright,
    caption,
    captionPlacement,
  } = props

  const sizes = sizesFromProps
    ? sizesFromProps
    : Object.entries(breakpoints)
        .map(([, value]) => `(max-width: ${value}px) ${value * 2}w`)
        .join(', ')

  let width: number | undefined = widthFromProps
  let height: number | undefined = heightFromProps
  let alt = altFromProps
  let src: StaticImageData | string = srcFromProps || ''

  if (!src && resource) {
    if (typeof resource === 'string') {
      src = resource
    } else if (typeof resource === 'object') {
      const { alt: altFromResource, height: fullHeight, url, width: fullWidth } = resource

      width = fullWidth!
      height = fullHeight!
      alt = altFromResource || ''

      const cacheTag = resource.updatedAt

      src = `${getClientSideURL()}${url}?${cacheTag}`
    }
  }
  if (!src) return

  return (
    <React.Fragment>
      <NextImage
        alt={alt || ''}
        className={cn(classNameFromProps)}
        fill={fill}
        height={!fill ? height : undefined}
        placeholder={priority ? undefined : 'blur'}
        blurDataURL={placeholderBlur}
        priority={priority}
        quality={100}
        loading={loading}
        sizes={sizes}
        src={src}
        style={styleFromProps}
        width={!fill ? width : undefined}
      />
      {(caption || copyright) && (
        <div
          className={cn('!text-body-XS-regular px-2 mt-2', {
            'text-text-inverted absolute bottom-2 inset-x-2 w-auto flex flex-row items-top justify-between':
              captionPlacement === 'inside',
            'w-auto flex flex-row items-top justify-between': captionPlacement === 'outside',
          })}
        >
          {caption ? <span className="text-text-base">{caption}</span> : <div></div>}
          {copyright && <span className="text-text-faded">© {copyright}</span>}
        </div>
      )}
    </React.Fragment>
  )
}
